import React from "react";
import { InputSearchIconSVG } from "@/assets/svg";
import { FilterSelectWithRadio } from "@/components/form";
import DateRangePicker from "@/components/form/DateRangePicker";

type Option = { value: string; label: string };

type DateRange = { startDate: string; endDate: string };

type EmailFiltersProps = {
  search: string;
  onSearchChange: (v: string) => void;

  clientOptions: Option[];
  projectOptions: Option[];
  scenarioOptions: Option[];
  statusOptions: Option[];

  clientFilter: string;
  onClientChange: (v: string) => void;
  projectFilter: string;
  onProjectChange: (v: string) => void;
  scenarioFilter: string;
  onScenarioChange: (v: string) => void;
  statusFilter: string;
  onStatusChange: (v: string) => void;

  dateRange: DateRange;
  tempDateRange: DateRange;
  setDateRange: (v: DateRange) => void;
  setTempDateRange: (v: DateRange) => void;
  onReset: () => void;
};

const EmailFilters: React.FC<EmailFiltersProps> = ({
  search,
  onSearchChange,
  clientOptions,
  projectOptions,
  scenarioOptions,
  statusOptions,
  clientFilter,
  onClientChange,
  projectFilter,
  onProjectChange,
  scenarioFilter,
  onScenarioChange,
  statusFilter,
  onStatusChange,
  dateRange,
  tempDateRange,
  setDateRange,
  setTempDateRange,
  onReset,
}) => {
  const hasAnyFilter =
    clientFilter !== "All clients" ||
    projectFilter !== "All projects" ||
    scenarioFilter !== "All scenarios" ||
    statusFilter !== "All statuses" ||
    !!dateRange.startDate ||
    !!dateRange.endDate ||
    !!search;

  return (
    <div className="py-4 flex flex-wrap items-center justify-between gap-2">
      <div className="relative flex-grow max-w-[246px]">
        <input
          type="text"
          placeholder="Search"
          className="w-full h-[42px] px-3 pl-9 bg-[#F9FBFF] rounded-[6px] focus:outline-none focus:border-[#73B5C5] placeholder:text-[#B5B7C0]"
          value={search}
          onChange={(e) => onSearchChange(e.target.value)}
        />
        <div className="absolute inset-y-0 left-0 flex items-center pl-2.5 pointer-events-none">
          <InputSearchIconSVG />
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-2">
        <div className="flex gap-3 mr-3">
          <div className="min-w-[143px]">
            <FilterSelectWithRadio
              options={clientOptions}
              width="w-[211px]"
              maxHeight="250px"
              showSearch
              searchPlaceholder="Search clients"
              value={clientFilter}
              onChange={onClientChange}
            />
          </div>
          <div className="min-w-[143px]">
            <FilterSelectWithRadio
              options={projectOptions}
              width="w-[211px]"
              maxHeight="250px"
              showSearch
              searchPlaceholder="Search projects"
              value={projectFilter}
              onChange={onProjectChange}
            />
          </div>
          <div className="min-w-[143px]">
            <FilterSelectWithRadio
              options={scenarioOptions}
              width="w-[211px]"
              maxHeight="250px"
              showSearch
              searchPlaceholder="Search scenarios"
              value={scenarioFilter}
              onChange={onScenarioChange}
            />
          </div>
          <div className="min-w-[143px]">
            <FilterSelectWithRadio
              options={statusOptions}
              value={statusFilter}
              onChange={onStatusChange}
            />
          </div>
          <div className="min-w-[220px] relative">
            <DateRangePicker
              startDate={tempDateRange.startDate || dateRange.startDate}
              endDate={tempDateRange.endDate || dateRange.endDate}
              onChange={(dates) => {
                setTempDateRange({
                  startDate: dates.startDate,
                  endDate: dates.endDate,
                });
                if (dates.startDate && dates.endDate) {
                  setDateRange({
                    startDate: dates.startDate,
                    endDate: dates.endDate,
                  });
                  setTempDateRange({ startDate: "", endDate: "" });
                }
              }}
            />
          </div>
        </div>

        <button
          className={`h-[38px] px-4 text-sm text-[#6C6C6C] bg-white rounded-[6px] ${
            hasAnyFilter
              ? "hover:bg-gray-50 cursor-pointer"
              : "opacity-50 cursor-not-allowed"
          }`}
          onClick={onReset}
          disabled={!hasAnyFilter}
        >
          Reset filters
        </button>
      </div>
    </div>
  );
};

export default EmailFilters;
